// WAAP Websites — the account's WAAP website list over the PUBLIC Open-API.
// Account-level: the `:id` in the path is an ANCHOR domain UUID that only resolves
// the customer session; the response is the whole website list.
//
//   GET /waap/domains/:anchorId/websites  → publicWebsiteDTO[]
//
// NOTE: this is the slimmer public shape — it has NO protection / protection-mode
// / certificate / status / siteConfigId metadata (those only exist on the private
// /cdn/domains surface). The list table shows only the fields available here.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
} from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/** One WAAP domain row (the Websites page). Mirrors the vendor wire shape. */
export interface WaapCdnDomain {
  /** Vendor domain id — shown as "Service ID". */
  id: number;
  name: string;
  alternateDomainNames?: string[];
  /** "Enabled" | "Disabled". */
  protectionStr: string;
  /** "intercept" (Block) | "monitor" (Monitor) | "" (disabled). */
  protectionMode: string;
  hasCert: boolean;
  active: boolean;
  waapEnabled: boolean;
  siteConfigId: number | null;
  /** Creation time, epoch milliseconds. */
  createdTimeStamp: number;
  deliveryDomain?: string;
}

/** The account's WAAP website list (PUBLIC Open-API). `anchorDomainId` is any of
 *  the service's domain UUIDs (used only to resolve the customer session). Returns
 *  the slimmer {@link WaapPublicWebsite} shape. */
export function useWaapCdnDomains(anchorDomainId: string | undefined) {
  return useQuery({
    queryKey: ['waap', 'cdn-domains', anchorDomainId ?? ''],
    enabled: !!anchorDomainId,
    retry: false,
    queryFn: async (): Promise<WaapPublicWebsite[]> => {
      const res = await serviceApiV3.get<WaapPublicWebsite[] | { data: WaapPublicWebsite[] }>(
        `/waap/domains/${anchorDomainId}/websites`,
      );
      const body = res.data as WaapPublicWebsite[] | { data?: WaapPublicWebsite[] };
      return Array.isArray(body) ? body : body?.data ?? [];
    },
  });
}

/** Create-website request body for the WAAP route. */
export interface CreateWaapWebsiteRequest {
  domain: string;
  origins: string[];
  alternate_domain_names?: string[];
  http2?: boolean;
  redirect_http_to_https?: boolean;
}

/** Create-website response. */
export interface CreateWaapWebsiteResult {
  id: string;
  domain: string;
  vendor_uid: string;
}

/** Creates a WAAP website (Eagle reverse-create) + persists the domain/binding.
 *  Creds + service_zone are resolved from `serviceId` (the WAAP service in the
 *  URL), so an account with no domains yet can still create its first website.
 *
 *   POST /services/:serviceId/waap/domains  → openapi/website.CreateWebsite
 *  (service-scoped, mirrors POST /services/:id/cdn/domains — :id is the service). */
export function useCreateWaapWebsite(
  serviceId: string | undefined,
  options?: UseMutationOptions<CreateWaapWebsiteResult, unknown, CreateWaapWebsiteRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateWaapWebsiteRequest) => {
      const res = await serviceApiV3.post<CreateWaapWebsiteResult>(
        `/services/${serviceId}/waap/domains`,
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      // The WAAP console sources its website list from the service's WAAP domain
      // registry (useServiceWaapDomainsPaged, key ['waap','service',serviceId,'domains',…]).
      // Invalidate it so a freshly created website appears without a hard reload.
      if (serviceId) void qc.invalidateQueries({ queryKey: ['waap', 'service', serviceId, 'domains'] });
      options?.onSuccess?.(...args);
    },
  });
}

/** One website on the PUBLIC Open-API surface (root conversant SDK, HMAC).
 *  Flatter than the private {@link WaapCdnDomain}: it carries delivery config
 *  (origins / headers / http2 / redirect / alternate domains) only — NO
 *  protection / certificate / status metadata. That is exactly why the Websites
 *  LIST stays on the private `cdn-domains` route: the table renders
 *  protectionStr / protectionMode / hasCert / active / waapEnabled, none of which
 *  exist here. Maps the BE `publicWebsiteDTO`. */
export interface WaapPublicWebsite {
  /** Vendor website id (numeric) — the `:websiteId` path segment. */
  id: number;
  service_id: number;
  domain: string;
  origins: string[];
  http2: boolean;
  redirect_http_to_https: boolean;
  headers: Record<string, string>;
  alternate_domain_names: string[];
  created: string;
  updated: string;
}

/** Update-website body (PUT). `origins` + `headers` are MANDATORY upstream — the
 *  vendor replaces the whole config, so always send the website's current
 *  origins/headers (read it first) even when only toggling http2/redirect.
 *  Maps the BE `publicWebsiteUpdateInput`. */
export interface UpdateWaapWebsiteRequest {
  origins: string[];
  headers: Record<string, string>;
  http2: boolean;
  redirect_http_to_https: boolean;
  alternate_domain_names?: string[];
  cc_protection_rule_id?: number;
}

/** Path for a single public website. `anchorId` is any of the account's domain
 *  UUIDs (resolves the customer session only); `websiteId` is the vendor numeric
 *  id from {@link WaapPublicWebsite.id} / the public list. */
const websitePath = (anchorId: string, websiteId: number | string) =>
  `/waap/domains/${anchorId}/websites/${websiteId}`;

/** Read one WAAP website over the PUBLIC Open-API (no consumer yet — added for
 *  parity with create; the live Websites detail uses the private aspect API).
 *
 *   GET /waap/domains/:anchorId/websites/:websiteId → WaapWebsite */
export function useGetWaapWebsite(
  anchorId: string | undefined,
  websiteId: number | string | undefined,
) {
  return useQuery({
    queryKey: ['waap', 'website', anchorId ?? '', String(websiteId ?? '')],
    enabled: !!anchorId && websiteId != null && websiteId !== '',
    retry: false,
    queryFn: async (): Promise<WaapPublicWebsite> => {
      const res = await serviceApiV3.get<WaapPublicWebsite>(
        websitePath(anchorId as string, websiteId as number | string),
      );
      return res.data;
    },
  });
}

/** Update (whole-config replace) a WAAP website over the PUBLIC Open-API.
 *  Invalidates the websites list + the single-website read.
 *
 *   PUT /waap/domains/:anchorId/websites/:websiteId → WaapWebsite */
export function useUpdateWaapWebsite(
  anchorId: string | undefined,
  websiteId: number | string,
  options?: UseMutationOptions<WaapPublicWebsite, unknown, UpdateWaapWebsiteRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateWaapWebsiteRequest) => {
      const res = await serviceApiV3.put<WaapPublicWebsite>(
        websitePath(anchorId as string, websiteId),
        body,
      );
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      void qc.invalidateQueries({ queryKey: ['waap', 'website', anchorId ?? ''] });
      options?.onSuccess?.(...args);
    },
  });
}

/** Delete a WAAP website over the PUBLIC Open-API. Invalidates the list.
 *
 *   DELETE /waap/domains/:anchorId/websites/:websiteId → { status } */
export function useDeleteWaapWebsite(
  anchorId: string | undefined,
  options?: UseMutationOptions<unknown, unknown, number | string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (websiteId: number | string) => {
      const res = await serviceApiV3.delete(websitePath(anchorId as string, websiteId));
      return res.data;
    },
    ...options,
    onSuccess: (...args) => {
      void qc.invalidateQueries({ queryKey: ['waap', 'cdn-domains'] });
      options?.onSuccess?.(...args);
    },
  });
}
